<template>
  <div class="journal-list-page">
    <PageHeader :user="user" @login="showLogin = true" @logout="doLogout" />
    <SloganBar :stats="stats || {}" />

    <!-- 搜索 -->
    <section class="search-section">
      <div class="search-inner">
        <input
          v-model="filters.name"
          type="text"
          class="search-input"
          placeholder="请输入关键字"
          @keyup.enter="load(1)"
        />
        <button type="button" class="btn-search" @click="load(1)">搜索</button>
        <button type="button" class="btn-reset" @click="resetFilters">重置</button>
      </div>
    </section>

    <!-- 筛选 -->
    <section class="filter-section">
      <button type="button" class="filter-toggle-btn" @click="showFilters = !showFilters">
        筛选 <span v-if="filterCount" class="filter-count">{{ filterCount }}</span>
      </button>
      <div class="filter-inner" :class="{ 'filter-expanded': showFilters }">
        <div class="filter-row">
          <span class="filter-label">文章方向：</span>
          <span class="filter-tags">
            <span
              v-for="(d, idx) in directions"
              :key="d.id != null ? d.id : 'dir-' + idx"
              class="filter-tag"
              :class="{ active: filters.direction_id === String(d.id) }"
              @click="toggleDirection(d.id)"
            >{{ d.name }}</span>
          </span>
        </div>
        <div class="filter-row">
          <span class="filter-label">期刊级别：</span>
          <span class="filter-tags">
            <span
              v-for="(o, idx) in levelOptions"
              :key="o.value || o.label || 'level-' + idx"
              class="filter-tag"
              :class="{ active: filters.level === o.value }"
              @click="filters.level = filters.level === o.value ? '' : o.value; load(1)"
            >{{ o.label }}</span>
          </span>
        </div>
        <div class="filter-row">
          <span class="filter-label">数据收录：</span>
          <span class="filter-tags">
            <span
              v-for="(o, idx) in indexDbOptions"
              :key="o.value || o.label || 'idxdb-' + idx"
              class="filter-tag"
              :class="{ active: filters.index_db === o.value }"
              @click="filters.index_db = filters.index_db === o.value ? '' : o.value; load(1)"
            >{{ o.label }}</span>
          </span>
        </div>
        <div class="filter-row">
          <span class="filter-label">期刊批次：</span>
          <span class="filter-tags">
            <span
              v-for="(o, idx) in batchOptions"
              :key="o.value || o.label || 'batch-' + idx"
              class="filter-tag"
              :class="{ active: filters.batch === o.value }"
              @click="filters.batch = filters.batch === o.value ? '' : o.value; load(1)"
            >{{ o.label }}</span>
          </span>
        </div>
        <div class="filter-row filter-row-inline">
          <span class="filter-label">出刊周期：</span>
          <select v-model="filters.publish_cycle" class="filter-select" @change="load(1)">
            <option value="">请选择</option>
            <option v-for="(o, idx) in publishCycleOptions" :key="o.value || 'cycle-' + idx" :value="o.value">{{ o.label }}</option>
          </select>
          <span class="filter-label">期刊页码：</span>
          <select v-model="filters.page_count" class="filter-select" @change="load(1)">
            <option value="">请选择</option>
          </select>
          <span class="filter-label">收录年份：</span>
          <select v-model="filters.include_year" class="filter-select" @change="load(1)">
            <option value="">请选择</option>
          </select>
        </div>
        <div class="filter-row filter-row-inline">
          <span class="filter-label">预算范围：</span>
          <input v-model.number="filters.budget_min" type="number" class="filter-input" placeholder="0" min="0" />
          <span class="filter-sep">-</span>
          <input v-model="filters.budget_max" type="text" class="filter-input" placeholder="请输入" />
          <button type="button" class="btn-search small" @click="load(1)">确定</button>
        </div>
        <div class="filter-row">
          <span class="filter-label">禁止推荐：</span>
          <span class="filter-tags">
            <span class="filter-tag">电子</span>
            <span class="filter-tag">报纸</span>
            <span class="filter-tag">国际</span>
            <span class="filter-tag">未上网</span>
          </span>
        </div>
        <div class="filter-row">
          <span class="filter-label">必须推荐：</span>
          <span class="filter-tags">
            <span class="filter-tag">XF</span>
            <span class="filter-tag">双刊号</span>
            <span class="filter-tag">带三要素</span>
            <span class="filter-tag">可查稿</span>
          </span>
        </div>
        <div class="filter-row">
          <span class="filter-label">推荐顺序：</span>
          <span class="filter-tags">
            <span
              class="filter-tag"
              :class="{ active: filters.order_type === 'publish_time' }"
              @click="setOrder('publish_time')"
            >出刊时间快→慢</span>
            <span
              class="filter-tag"
              :class="{ active: filters.order_type === 'price' }"
              @click="setOrder('price')"
            >期刊价格低→高</span>
            <span
              class="filter-tag"
              :class="{ active: filters.order_type === 'difficulty' }"
              @click="setOrder('difficulty')"
            >审稿难度低→高</span>
          </span>
        </div>
        <div class="filter-row">
          <span class="filter-label">热门推荐：</span>
          <span class="filter-tags">
            <span
              class="filter-tag highlight"
              :class="{ active: filters.is_hot === 1 }"
              @click="filters.is_hot = filters.is_hot === 1 ? '' : 1; load(1)"
            >热门推荐</span>
            <span class="filter-tag">行业/专刊目录</span>
            <span class="filter-tag">学会/协会主办</span>
            <span class="filter-tag">报纸专区</span>
            <span class="filter-tag">核心/书评/作品/微论</span>
            <span class="filter-tag">荣誉头衔</span>
            <span class="filter-tag">收稿方向</span>
            <span class="filter-tag">营销标签</span>
            <span
              class="filter-tag"
              :class="{ active: filters.is_self_operated === 1 }"
              @click="filters.is_self_operated = filters.is_self_operated === 1 ? '' : 1; load(1)"
            >自营期刊</span>
          </span>
        </div>
      </div>
    </section>

    <!-- 期刊列表 三列网格 -->
    <section class="list-section">
      <div class="list-inner">
        <div class="journal-grid">
          <JournalCard
            v-for="(j, idx) in list"
            :key="j.id != null ? j.id : 'j-' + idx"
            :journal="j"
            @click="openDetail(j.id)"
          />
        </div>
        <div v-if="loading" class="loading-tip">加载中…</div>
        <div v-else-if="noMore && list.length" class="loading-tip">没有更多了</div>
        <div v-else-if="!list.length && !loading" class="empty">暂无期刊</div>
        <div v-if="list.length && !noMore" class="load-more-wrap">
          <button type="button" class="btn-load-more" @click="loadMore">加载更多</button>
        </div>
      </div>
    </section>

    <footer class="site-footer">
      <div class="footer-inner">
        [刊期日更网]  支持关键词找您想要的期刊，信息应有尽有
      </div>
    </footer>

    <JournalDetail
      :visible="detailVisible"
      :detail="detail"
      @close="detailVisible = false"
    />

    <!-- 登录弹窗 -->
    <div v-if="showLogin" class="login-overlay" @click.self="showLogin = false">
      <div class="login-modal">
        <div class="modal-header">
          <h3>{{ showRegister ? '注册' : '登录' }}</h3>
          <button type="button" class="modal-close" @click="showLogin = false; showRegister = false">&times;</button>
        </div>
        <div class="modal-body">
          <template v-if="!showRegister">
            <div class="form-item">
              <label>账号（用户名或手机）</label>
              <input v-model="loginForm.account" type="text" placeholder="请输入账号" />
            </div>
            <div class="form-item">
              <label>密码</label>
              <input v-model="loginForm.password" type="password" placeholder="请输入密码" />
            </div>
            <button type="button" class="btn-search full" :disabled="loginLoading" @click="doLogin">登录</button>
            <p class="form-tip">没有账号? <a href="javascript:;" @click.prevent="showRegister = true">点击注册</a></p>
          </template>
          <template v-else>
            <div class="form-item">
              <label>用户名</label>
              <input v-model="registerForm.username" type="text" placeholder="请输入用户名" />
            </div>
            <div class="form-item">
              <label>手机号</label>
              <input v-model="registerForm.mobile" type="text" placeholder="请输入手机号" />
            </div>
            <div class="form-item">
              <label>密码</label>
              <input v-model="registerForm.password" type="password" placeholder="6-32位密码" />
            </div>
            <div class="form-item">
              <label>确认密码</label>
              <input v-model="registerForm.password2" type="password" placeholder="再次输入密码" />
            </div>
            <button type="button" class="btn-search full" :disabled="registerLoading" @click="doRegister">注册</button>
            <p class="form-tip">已有账号? <a href="javascript:;" @click.prevent="showRegister = false">去登录</a></p>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import warmApi from '../api/warm'
import authApi from '../api/auth'
import PageHeader from '../components/PageHeader.vue'
import SloganBar from '../components/SloganBar.vue'
import JournalCard from '../components/JournalCard.vue'
import JournalDetail from '../components/JournalDetail.vue'

export default {
  name: 'JournalList',
  components: { PageHeader, SloganBar, JournalCard, JournalDetail },
  setup() {
    const stats = ref(null)
    const directions = ref([])
    const options = ref({})
    const list = ref([])
    const page = ref(1)
    const limit = 12
    const totalCount = ref(0)
    const loading = ref(false)
    const detailVisible = ref(false)
    const detail = ref({})
    const showLogin = ref(false)
    const showRegister = ref(false)
    const showFilters = ref(false)
    const user = ref(null)
    const loginForm = reactive({ account: '', password: '' })
    const registerForm = reactive({ username: '', mobile: '', password: '', password2: '' })
    const loginLoading = ref(false)
    const registerLoading = ref(false)

    const filters = reactive({
      name: '',
      direction_id: '',
      level: '',
      index_db: '',
      batch: '',
      publish_cycle: '',
      page_count: '',
      include_year: '',
      budget_min: '',
      budget_max: '',
      order_type: '',
      order_sort: 'asc',
      is_hot: '',
      is_self_operated: ''
    })

    const levelOptions = computed(() => (options.value.level || []).map(o => ({ value: o.value || o.label, label: o.label })))
    const indexDbOptions = computed(() => (options.value.index_db || []).map(o => ({ value: o.value || o.label, label: o.label })))
    const batchOptions = computed(() => (options.value.batch || []).map(o => ({ value: o.value || o.label, label: o.label })))
    const publishCycleOptions = computed(() => (options.value.publish_cycle || []).map(o => ({ value: o.value || o.label, label: o.label })))

    async function loadFilter() {
      try {
        const res = await warmApi.getFilter()
        directions.value = res?.directions ?? []
        options.value = res?.options ?? {}
      } catch (e) {
        try {
          directions.value = await warmApi.getDirections()
          options.value = await warmApi.getOptions()
        } catch (e2) {
          console.warn('filter/directions/options', e2)
        }
      }
    }

    async function loadStats() {
      try {
        stats.value = await warmApi.getStats()
      } catch (e) {
        console.warn('stats', e)
      }
    }

    function toggleDirection(id) {
      const sid = String(id)
      filters.direction_id = filters.direction_id === sid ? '' : sid
      load(1)
    }

    function setOrder(type) {
      filters.order_type = filters.order_type === type ? '' : type
      filters.order_sort = type === 'price' ? 'asc' : 'asc'
      load(1)
    }

    function resetFilters() {
      filters.name = ''
      filters.direction_id = ''
      filters.level = ''
      filters.index_db = ''
      filters.batch = ''
      filters.publish_cycle = ''
      filters.budget_min = ''
      filters.budget_max = ''
      filters.order_type = ''
      filters.is_hot = ''
      filters.is_self_operated = ''
      load(1)
    }

    async function load(p = 1) {
      if (loading.value) return
      loading.value = true
      page.value = p
      const params = {
        page: page.value,
        limit,
        name: filters.name || undefined,
        direction_id: filters.direction_id || undefined,
        level: filters.level || undefined,
        index_db: filters.index_db || undefined,
        batch: filters.batch || undefined,
        publish_cycle: filters.publish_cycle || undefined,
        order_type: filters.order_type || undefined,
        order_sort: filters.order_sort || undefined,
        is_hot: filters.is_hot !== '' ? filters.is_hot : undefined,
        is_self_operated: filters.is_self_operated !== '' ? filters.is_self_operated : undefined
      }
      if (filters.budget_min !== '' && filters.budget_min != null) params.budget_min = filters.budget_min
      if (filters.budget_max !== '' && filters.budget_max != null) params.budget_max = Number(filters.budget_max) || undefined
      try {
        const res = await warmApi.getJournals(params)
        const arr = res.list || []
        totalCount.value = res.count ?? 0
        if (p === 1) list.value = arr
        else list.value = [...list.value, ...arr]
      } catch (e) {
        if (p === 1) list.value = []
        console.warn('journals', e)
      } finally {
        loading.value = false
      }
    }

    const noMore = computed(() => list.value.length >= totalCount.value && totalCount.value > 0)
    const filterCount = computed(() => {
      let n = 0
      if (filters.direction_id) n++
      if (filters.level) n++
      if (filters.index_db) n++
      if (filters.batch) n++
      if (filters.publish_cycle) n++
      if (filters.order_type) n++
      if (filters.is_hot === 1) n++
      if (filters.is_self_operated === 1) n++
      if (filters.budget_min !== '' && filters.budget_min != null) n++
      if (filters.budget_max !== '' && filters.budget_max != null) n++
      return n
    })

    function loadMore() {
      if (loading.value || noMore.value) return
      load(page.value + 1)
    }

    async function openDetail(id) {
      detailVisible.value = true
      detail.value = {}
      try {
        detail.value = await warmApi.getDetail(id)
      } catch (e) {
        console.warn('detail', e)
      }
    }

    async function doLogin() {
      if (!loginForm.account.trim() || !loginForm.password) {
        alert('请输入账号和密码')
        return
      }
      loginLoading.value = true
      try {
        const res = await authApi.login(loginForm.account.trim(), loginForm.password)
        authApi.setStoredToken(res.token)
        user.value = res.user
        showLogin.value = false
        loginForm.account = ''
        loginForm.password = ''
        load(1)
      } catch (e) {
        alert(e.message || '登录失败')
      } finally {
        loginLoading.value = false
      }
    }

    async function doRegister() {
      const { username, mobile, password, password2 } = registerForm
      if (!username.trim()) { alert('请输入用户名'); return }
      if (!mobile.trim()) { alert('请输入手机号'); return }
      if (!password) { alert('请输入密码'); return }
      if (password !== password2) { alert('两次密码不一致'); return }
      if (password.length < 6) { alert('密码至少6位'); return }
      registerLoading.value = true
      try {
        const res = await authApi.register({
          username: username.trim(),
          mobile: mobile.trim(),
          password
        })
        authApi.setStoredToken(res.token)
        user.value = res.user
        showLogin.value = false
        showRegister.value = false
        registerForm.username = ''
        registerForm.mobile = ''
        registerForm.password = ''
        registerForm.password2 = ''
        load(1)
      } catch (e) {
        alert(e.message || '注册失败')
      } finally {
        registerLoading.value = false
      }
    }

    async function doLogout() {
      try {
        await authApi.logout()
      } catch (_) { /* ignore */ }
      user.value = null
      load(1)
    }

    onMounted(() => {
      loadStats()
      loadFilter()
      load(1)
      if (typeof window !== 'undefined' && window.innerWidth > 768) showFilters.value = true
      const token = authApi.getStoredToken()
      if (token) {
        authApi.userInfo()
          .then(u => { user.value = u })
          .catch(() => { authApi.setStoredToken(''); user.value = null })
      }
    })

    return {
      stats,
      directions,
      filters,
      levelOptions,
      indexDbOptions,
      batchOptions,
      publishCycleOptions,
      list,
      loading,
      noMore,
      detailVisible,
      detail,
      user,
      showLogin,
      showRegister,
      showFilters,
      filterCount,
      loginForm,
      registerForm,
      loginLoading,
      registerLoading,
      toggleDirection,
      setOrder,
      resetFilters,
      load,
      loadMore,
      openDetail,
      doLogin,
      doRegister,
      doLogout
    }
  }
}
</script>

<style scoped>
.journal-list-page { padding-bottom: 40px; min-height: 100vh; background: var(--color-bg); padding-bottom: calc(40px + var(--safe-bottom)); }
.search-section {
  background: #fff;
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-border);
}
.search-inner {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  gap: 12px;
  align-items: center;
}
.search-input {
  flex: 1;
  max-width: 600px;
  min-width: 0;
  padding: 10px 14px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  font-size: 14px;
}
.btn-search {
  padding: 10px 24px;
  border: none;
  border-radius: var(--radius);
  background: var(--color-primary);
  color: #fff;
  font-size: 14px;
  min-height: var(--touch-min);
}
.btn-search.small { padding: 6px 14px; font-size: 12px; min-height: 0; }
.btn-reset {
  padding: 10px 24px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  background: #fff;
  color: var(--color-text-secondary);
  font-size: 14px;
  min-height: var(--touch-min);
}
.filter-section {
  background: #fff;
  padding: 16px 20px;
  margin-bottom: 16px;
  border-bottom: 1px solid var(--color-border);
}
.filter-toggle-btn {
  display: none;
  width: 100%;
  padding: 12px 16px;
  margin-bottom: 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  background: #fff;
  font-size: 15px;
  color: var(--color-text);
  min-height: var(--touch-min);
}
.filter-count {
  display: inline-block;
  margin-left: 6px;
  padding: 0 6px;
  min-width: 20px;
  height: 20px;
  line-height: 20px;
  text-align: center;
  font-size: 12px;
  background: var(--color-primary);
  color: #fff;
  border-radius: 10px;
}
.filter-inner { max-width: 1200px; margin: 0 auto; }
.filter-row-inline {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.filter-select {
  padding: 6px 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  font-size: 13px;
  min-width: 100px;
}
.filter-input {
  width: 80px;
  padding: 6px 8px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  font-size: 13px;
}
.filter-sep { color: #999; }
.filter-tag.highlight { border-color: var(--color-primary); color: var(--color-primary); }
.filter-tag.highlight.active { background: var(--color-primary); color: #fff; }
.list-section { padding: 0 20px 20px; }
.list-inner { max-width: 1200px; margin: 0 auto; }
.journal-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

@media (max-width: 768px) {
  .search-section { padding: 12px 12px; }
  .search-inner { flex-wrap: wrap; }
  .search-input { max-width: none; flex: 1 1 100%; }
  .btn-search, .btn-reset { flex: 1; min-width: 0; }
  .filter-toggle-btn { display: block; }
  .filter-inner.filter-expanded { display: block; }
  .filter-section { padding: 12px; }
  .list-section { padding: 0 12px 20px; }
}
@media (max-width: 768px) {
  .filter-inner:not(.filter-expanded) { display: none; }
}
@media (max-width: 900px) {
  .journal-grid { grid-template-columns: repeat(2, 1fr); gap: 14px; }
}
@media (max-width: 600px) {
  .journal-grid { grid-template-columns: 1fr; gap: 12px; }
}
.loading-tip, .empty { text-align: center; color: #999; font-size: 13px; padding: 24px; }
.load-more-wrap { text-align: center; padding: 20px; }
.btn-load-more {
  padding: 12px 32px;
  min-height: var(--touch-min);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  background: #fff;
  color: var(--color-text-secondary);
  font-size: 14px;
}
.login-overlay {
  position: fixed;
  inset: 0;
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,.5);
}
.login-modal {
  background: #fff;
  border-radius: 8px;
  max-width: 400px;
  width: 90%;
  overflow: hidden;
}
.login-modal .modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.login-modal .modal-header h3 { margin: 0; font-size: 16px; }
.login-modal .modal-body { padding: 20px; }
.form-item { margin-bottom: 14px; }
.form-item label { display: block; margin-bottom: 4px; font-size: 13px; color: #666; }
.form-item input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  font-size: 14px;
  box-sizing: border-box;
}
.btn-search.full { width: 100%; margin-top: 8px; }
.form-tip { margin-top: 12px; font-size: 13px; color: #999; text-align: center; }
.form-tip a { color: var(--color-primary); }
.text-muted { color: #999; }
.site-footer {
  margin-top: 24px;
  padding: 16px 20px;
  background: #fff;
  border-top: 1px solid var(--color-border);
  font-size: 12px;
  color: var(--color-text-muted);
  text-align: center;
}
.footer-inner { max-width: 1200px; margin: 0 auto; }
</style>
